/**
 * Office Hours' week, said the way a person says it — prototype `ohSummary`:
 * consecutive days on the same hours collapse ("Mon–Thu 2–4 PM · Fri 10 AM–
 * 12 PM"), and an hour drops its ":00".
 *
 * The rota is kept in the hub's wall clock ('HH:mm'). Given the hub's zone
 * and the viewer's, each day's hours and weekday are moved onto the viewer's clock, as
 * everything else on both sides is (XN-4673).
 */
import { convertClock } from './sessionZone';

const SHORT = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };

const CLOSED_ALL_WEEK = 'Closed — nobody is on duty on any day';

const parts = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return { hour: h % 12 === 0 ? 12 : h % 12, minute: m, meridiem: h < 12 ? 'AM' : 'PM' };
};

const short = ({ hour, minute }) => (minute ? `${hour}:${String(minute).padStart(2, '0')}` : `${hour}`);

/** "2–4 PM", "10 AM–12 PM", "9:30–11 AM". */
const hoursLabel = (from, to) => {
  const a = parts(from);
  const b = parts(to);

  return a.meridiem === b.meridiem
    ? `${short(a)}–${short(b)} ${b.meridiem}`
    : `${short(a)} ${a.meridiem}–${short(b)} ${b.meridiem}`;
};



/** The next date falling on an ISO weekday, as 'YYYY-MM-DD', for a conversion that respects that day's offset. */
const nextOn = (weekday, now = new Date()) => {
  const date = new Date(now);
  const iso = ((date.getDay() + 6) % 7) + 1;
  date.setDate(date.getDate() + ((weekday - iso + 7) % 7));
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const moved = (day, fromZone, toZone, now) => {
  if (!fromZone || !toZone || fromZone === toZone) return day;
  const on = nextOn(day.weekday, now);
  const converted = convertClock(`${on} ${day.from}:00`, fromZone, toZone);
  const delta = Math.round((new Date(`${converted.slice(0, 10)}T12:00:00Z`) - new Date(`${on}T12:00:00Z`)) / 86400000);
  const weekday = (((day.weekday - 1 + delta) % 7 + 7) % 7) + 1;
  const to = convertClock(`${on} ${day.to}:00`, fromZone, toZone).slice(11, 16);

  return { ...day, weekday, from: converted.slice(11, 16), to };
};

const byWeekdayAndStart = (a, b) => a.weekday - b.weekday || a.from.localeCompare(b.from);

/**
 * @param {Array<{weekday: number, from: string, to: string}>} days  the open days
 * @param {{fromZone?: string, toZone?: string, now?: Date}} zones  the rota's zone, the viewer's, and the
 *   card's clock, whose coming week sets each day's offset
 */
export function weekSummary(days, { fromZone, toZone, now } = {}) {
  const open = (days ?? [])
    .filter((day) => day?.from && day?.to)
    .map((day) => moved(day, fromZone, toZone, now))
    .sort(byWeekdayAndStart);

  if (!open.length) return CLOSED_ALL_WEEK;

  const runs = [];
  for (const day of open) {
    const last = runs[runs.length - 1];
    if (last && last.from === day.from && last.to === day.to && last.end === day.weekday - 1) {
      last.end = day.weekday;
      last.days.push(day.weekday);
    } else {
      runs.push({ from: day.from, to: day.to, end: day.weekday, days: [day.weekday] });
    }
  }

  return runs.map((run) => {
    const names = run.days.map((weekday) => SHORT[weekday]);
    const label = names.length === 1 ? names[0]
      : names.length === 2 ? names.join(' & ')
        : `${names[0]}–${names[names.length - 1]}`;

    return `${label} ${hoursLabel(run.from, run.to)}`;
  }).join(' · ');
}
